class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (window.location.hash === '#cart-open') {
      this.open();
    }
    this.onCartRendered();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  initAccordions() {
    if (this._accordionHandler) return;
    this._accordionHandler = (event) => {
      const title = event.target.closest('.cart-accordion-item .accordion-title');
      if (!title) return;
      const accordionItem = title.closest('.cart-accordion-item');
      const content = accordionItem.querySelector('.accordion-content');
      if (!content) return;
      const isOpen = accordionItem.classList.contains('open');
      this.querySelectorAll('.cart-accordion-item.open').forEach(openItem => {
        if (openItem !== accordionItem) {
          openItem.classList.remove('open');
          openItem.querySelector('.accordion-content').classList.remove('open');
        }
      });
      accordionItem.classList.toggle('open', !isOpen);
      content.classList.toggle('open', !isOpen);
    };
    this.addEventListener('click', this._accordionHandler);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      try {
        const sectionEl = document.getElementById(section.id);
        const selectors = section.selector?.split(',').map(s => s.trim());
        const sectionElements = selectors ? selectors.map((selector) => document.querySelector(selector)) : [sectionEl];

        if (!sectionElements || !sectionElements.length) return;
        if (!parsedState.sections?.[section.id]) return;
        const newHtml = this.getSectionInnerHTML(parsedState.sections[section.id], selectors);
        sectionElements.forEach((sectionElement, index) => {
          if (!sectionElement || !newHtml[index]) return;
          sectionElement.innerHTML = newHtml[index];
        });
      } catch (e) {
        console.warn(`[CartDrawer] Failed to update section ${section.id}:`, e);
      }
    });

    const isEmpty = parsedState.item_count === 0;
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('.drawer__inner').classList.toggle('is-empty', isEmpty);
    this.querySelector('cart-drawer-items').classList.toggle('is-empty', isEmpty);

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.open();
    this.onCartRendered();
  }

  onCartRendered() {
    this.initAccordions();
    this.initUpsellSwiper();
    this.updateUpsellRecommendations();
    window.yotpo?.refreshWidgets();

    document.querySelectorAll('quantity-input').forEach(qtyInput => {
      if (qtyInput.querySelector('input') && !qtyInput.input) qtyInput.connectedCallback?.();
    });

    document.querySelectorAll('subscription-toggle').forEach(toggle => {
      if (toggle.querySelector('input[id^="subscription-"]') && !toggle.input) toggle.connectedCallback?.();
    });

    document.querySelectorAll('cart-drawer-items').forEach(drawerItems => {
      drawerItems.initDropdowns?.();
    });
  }

  initUpsellSwiper() {
    if (typeof Swiper === 'undefined') return;
    const container = this.querySelector('.cart-drawer__upsell-items-container.swiper-container');
    if (!container) return;
    this.upsellSwiper?.destroy(true, true);
    this.upsellSwiper = new Swiper(container, {
      spaceBetween: 4,
      slidesPerView: 1.1,
      loop: false,
      navigation: {
        nextEl: container.querySelector('.cart-swiper-button-next'),
        prevEl: container.querySelector('.cart-swiper-button-prev'),
      },
      slidesPerGroup: 1,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
        thresholdDelta: 6,
        thresholdTime: 500,
      }, breakpoints: {
        640: {
          slidesPerView: 1.2,
          spaceBetween: 6
        }
      }
    });
  }

  async updateUpsellRecommendations() {
    const TAG = '[upsell-recs]';
    console.log(`${TAG} updateUpsellRecommendations() called`);

    const upsell = this.querySelector('.cart-drawer__upsell');
    if (!upsell) {
      console.log(`${TAG} ABORT: no .cart-drawer__upsell element (upsell disabled or not rendered)`);
      return;
    }

    const {
      recommendUrl,
      recommendSection,
      recommendIntent = 'complementary',
      recommendFallbackIntent = '',
      recommendLimit = '10',
    } = upsell.dataset;
    console.log(`${TAG} config`, { recommendUrl, recommendSection, recommendIntent, recommendFallbackIntent, recommendLimit });
    if (!recommendUrl || !recommendSection) {
      console.log(`${TAG} ABORT: missing data-recommend-url or data-recommend-section`);
      return;
    }

    const productId = upsell.querySelector('.cart-drawer__upsell-inner')?.dataset.cartFirstProduct;
    console.log(`${TAG} anchor product_id (cart.items.first) =`, productId);
    if (!productId) {
      console.log(`${TAG} ABORT: no anchor product id — keeping static fallback`);
      return;
    }

    const wrapper = upsell.querySelector('.cart-drawer__upsell-items.swiper-wrapper');
    if (!wrapper) {
      console.log(`${TAG} ABORT: no .swiper-wrapper to inject into`);
      return;
    }

    // Try each intent in order (e.g. complementary, then related); first non-empty wins.
    const intents = [recommendIntent, recommendFallbackIntent].filter((v, i, arr) => v && arr.indexOf(v) === i);
    let slides = [];
    let usedIntent = null;
    for (const intent of intents) {
      slides = await this.fetchRecommendationSlides(recommendUrl, recommendSection, productId, intent, recommendLimit);
      if (slides.length) {
        usedIntent = intent;
        break;
      }
    }

    if (!slides.length) {
      console.log(`${TAG} no recommendations from [${intents.join(', ')}] — keeping static fallback`);
      return;
    }

    wrapper.querySelectorAll('.swiper-slide').forEach(slide => slide.remove());
    const fragment = document.createDocumentFragment();
    slides.forEach(slide => fragment.appendChild(slide));
    wrapper.insertBefore(fragment, wrapper.firstChild);

    this.initUpsellSwiper();
    window.yotpo?.refreshWidgets();
    console.log(`${TAG} SUCCESS: swapped in ${slides.length} card(s) from intent="${usedIntent}"`);
  }

  async fetchRecommendationSlides(url, sectionId, productId, intent, limit) {
    const TAG = '[upsell-recs]';
    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: productId,
      intent,
      limit,
    });
    const fetchUrl = `${url}?${params}`;
    console.log(`${TAG} fetching (intent=${intent})`, fetchUrl);

    try {
      const response = await fetch(fetchUrl);
      console.log(`${TAG} response (intent=${intent})`, response.status, response.ok);
      if (!response.ok) return [];

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const slides = Array.from(doc.querySelectorAll('.swiper-slide'));
      console.log(`${TAG} cards returned (intent=${intent}):`, slides.length);
      return slides;
    } catch (e) {
      console.warn(`${TAG} fetch failed (intent=${intent}):`, e);
      return [];
    }
  }

  getSectionInnerHTML(html, selectors = ['.shopify-section']) {
    const htmlDoc = new DOMParser().parseFromString(html, 'text/html');
    return selectors.map((selector) => htmlDoc.querySelector(selector)?.innerHTML ?? null);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.drawer__header, cart-drawer-items, .drawer__footer, .drawer__inner-empty, .cart-drawer__upsell, .cart-drawer__progress',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async updateCartAttributes(attributes) {
    try {
      const response = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
      const data = await response.json();
      publish(PUB_SUB_EVENTS.cartUpdate, { cartData: data });
    } catch (error) {
      console.error('Failed to update cart attributes:', error);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.throttledSellingPlanClick = throttle(this.handleSellingPlanClick.bind(this), 1000);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.onKeyup = this.onKeyup.bind(this);
    this.onTriggerKeyup = this.onTriggerKeyup.bind(this);
    this.destroyDropdowns = this.destroyDropdowns.bind(this);

    document.addEventListener('click', this.handleOutsideClick);
  }

  connectedCallback() {
    super.connectedCallback();
    this.initDropdowns();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__header, cart-drawer-items, .drawer__footer, .drawer__inner-empty, .cart-drawer__progress',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  initDropdowns() {
    if (this.dropdowns) this.destroyDropdowns();

    this.dropdowns = this.querySelectorAll('.cart-subscription-dropdown');

    if (this.dropdowns?.length) {
      this.dropdowns.forEach(this.initDropdown.bind(this));
    }
    this.activeDropdown = null;
  }

  initDropdown(dropdown) {
    if (!dropdown) return;

    if (!this.throttledSellingPlanClick) {
      this.throttledSellingPlanClick = throttle(this.handleSellingPlanClick.bind(this), 1000);
    }

    const trigger = dropdown.querySelector('.cart-subscription-dropdown-trigger');
    const itemsWrapper = dropdown.querySelector('.cart-subscription-dropdown-items-wrapper');
    const itemsInner = dropdown.querySelector('.cart-subscription-dropdown-items');
    const items = itemsInner.querySelectorAll('.cart-subscription-dropdown-item');
    const sellingPlanQty = dropdown.dataset.sellingPlanQty || 1;

    const props = {
      trigger,
      itemsWrapper,
      itemsInner,
      items,
      fullHeight: itemsInner.offsetHeight,
      isOpen: false,
      sellingPlanQty: sellingPlanQty,
      _onTriggerClick: (event) => this.toggleDropdown(event, dropdown),
      _onTriggerKeyup: (event) => this.onTriggerKeyup(event, dropdown),
      _onOptionClick: (event) => this.throttledSellingPlanClick(event, dropdown),
      _onKeyup: (event) => this.onKeyup(event, dropdown),
    };

    Object.assign(dropdown, props);

    trigger.addEventListener('click', props._onTriggerClick);
    trigger.addEventListener('keyup', props._onTriggerKeyup);
    itemsInner.addEventListener('click', props._onOptionClick);
    itemsInner.addEventListener('keyup', props._onKeyup);
  }

  toggleDropdown(event, dropdown) {
    event.preventDefault();
    event.stopPropagation();
    if (dropdown.classList.contains('disabled')) return;

    dropdown.isOpen ? this.closeDropdown(dropdown) : this.openDropdown(dropdown);
  }

  openDropdown(dropdown) {
    if (this.activeDropdown && this.activeDropdown !== dropdown) {
      this.closeDropdown(this.activeDropdown);
    }

    dropdown.isOpen = true;
    dropdown.classList.add('active');
    dropdown.itemsWrapper.style.height = `${dropdown.fullHeight}px`;
    dropdown.trigger.setAttribute('aria-expanded', 'true');

    dropdown.items.forEach((item) => {
      item.setAttribute('tabindex', '0');
    });

    this.activeDropdown = dropdown;
  }

  closeDropdown(dropdown) {
    dropdown.isOpen = false;
    dropdown.classList.remove('active');
    dropdown.itemsWrapper.style.height = '0px';
    dropdown.trigger.setAttribute('aria-expanded', 'false');

    dropdown.items.forEach((item) => {
      item.setAttribute('tabindex', '-1');
    });

    if (this.activeDropdown === dropdown) {
      this.activeDropdown = null;
    }
  }

  onTriggerKeyup(e, dropdown) {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      dropdown.trigger.focus();
      this.closeDropdown(dropdown);
    }
  }

  onKeyup(e, dropdown) {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeDropdown(dropdown);
      dropdown.trigger.focus();
    }

    if (e.key == 'Enter') {
      e.preventDefault();
      this.handleSellingPlanClick(e, dropdown);
    }
  }

  handleSellingPlanClick(e, dropdown) {
    if (e.target.tagName !== 'SPAN') return;
    e.preventDefault();
    e.stopPropagation();

    const index = e.target.dataset.index;
    const sellingPlanQty = dropdown.sellingPlanQty || 1;
    const sellingPlanId = e.target.dataset.sellingPlanId;

    const selectedPlanId = e.target.closest('.cart-subscription-dropdown')?.dataset?.selectedPlanId;

    if (selectedPlanId == sellingPlanId) return;
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

    this.closeDropdown(dropdown);
    cartItems.updateQuantity(index, sellingPlanQty, e, 'subscription', sellingPlanId);
  }

  handleOutsideClick(e) {
    if (!this.activeDropdown) return;

    const activeWrapper = this.activeDropdown.querySelector('.cart-subscription-dropdown-items-wrapper');
    if (!activeWrapper.contains(e.target)) {
      this.closeDropdown(this.activeDropdown);
    }
  }

  clearDropdownEvents(dropdown) {
    if (!dropdown) return;

    if (dropdown.trigger) {
      if (typeof dropdown._onTriggerClick === 'function') {
        dropdown.trigger.removeEventListener('click', dropdown._onTriggerClick);
      }
      if (typeof dropdown._onTriggerKeyup === 'function') {
        dropdown.trigger.removeEventListener('keyup', dropdown._onTriggerKeyup);
      }
    }
    if (dropdown.itemsInner) {
      if (typeof dropdown._onKeyup === 'function') {
        dropdown.itemsInner.removeEventListener('keyup', dropdown._onKeyup);
      }
      if (typeof dropdown._onOptionClick === 'function') {
        dropdown.itemsInner.removeEventListener('click', dropdown._onOptionClick);
      }
    }
  }

  destroyDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      this.clearDropdownEvents(dropdown);
    });
    this.dropdowns = null;
    this.activeDropdown = null;
  }

  disconnectedCallback() {
    this.destroyDropdowns();
    document.removeEventListener('click', this.handleOutsideClick);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartCount extends HTMLElement {
  constructor() {
    super();

    this.countText = this.querySelector('.count-text') || this;
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.updateCount.bind(this));
  }

  updateCount({ cartData }) {
    const itemCount = cartData.item_count;
    this.countText.textContent = itemCount;
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber();
  }
}

customElements.define('cart-count', CartCount);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    this.bar = this.querySelector('.progress-bar__bar');

    this.target = this.querySelector('.progress-target');
    this.textEl = this.querySelector('.progress-bar__text');
    this.initialProgress = Number(this.dataset.initialProgress);

    this.type = this.dataset.type;
    this.finalTarget = Number(this.dataset.finalTarget);
    this.currentProgressState = this.dataset.currentProgress || 'none';
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.updateProgress.bind(this));

    this.messages = {
      toReach: '',
      complete: '',
    };
    this.loadMessages();
  }

  connectedCallback() {
    if (!isNaN(this.initialProgress) && this.bar) {
      const initialProgress = Math.min(100, Math.max(0, this.initialProgress));
      this.bar.setAttribute('style', `--progress: ${initialProgress.toFixed(2)}%`);

      if (this.target) {
        const targetProgress = parseFloat(this.target.dataset.progress) || 0;
        this.target.classList.toggle('completed', initialProgress >= targetProgress);
      }

      const initialTotal = parseInt(this.dataset.cartTotal) || 0;
      this.updateText(initialTotal);
    } else {
      const initialTotal = parseInt(this.dataset.cartTotal);
      if (!isNaN(initialTotal)) {
        this.updateProgress({ cartData: { total_price: initialTotal } });
      }
    }
  }

  loadMessages() {
    const stringBase = window.cartStrings;
    if (!stringBase) return;

    switch (this.type) {
      case 'shipping':
        this.messages.toReach = stringBase.progressShippingHtml;
        this.messages.complete = stringBase.progressShippingCompleteHtml;
        break;
    }
  }

  getMessage(progressState, currentPrice) {
    const remaining = parseInt(this.finalTarget) - currentPrice;
    switch (progressState) {
      case 'none':
        return (this.messages.toReach || '').replace('[amount]', Shopify.formatMoney(remaining));
      case 'completed':
        return (this.messages.complete || '').replace('[amount]', Shopify.formatMoney(remaining));
      default:
        return (this.messages.toReach || '').replace('[amount]', Shopify.formatMoney(remaining));
    }
  }

  updateProgress({ cartData }) {
    const price = parseInt(cartData.total_price) || 0;
    const finalTarget = parseInt(this.finalTarget) || 0;

    if (finalTarget === 0) return;

    const progress = Math.min(100, (price / finalTarget) * 100);

    this.bar.setAttribute('style', `--progress: ${progress.toFixed(2)}%`);

    this.updateText(price);

    if (this.target) {
      const targetProgress = parseFloat(this.target.dataset.progress) || 0;
      this.target.classList.toggle('completed', progress >= targetProgress);
    }
  }

  async updateText(price) {
    if (!this.textEl) return;

    const progressState = price >= this.finalTarget ? 'completed' : 'none';
    const message = this.getMessage(progressState, price);

    if (progressState === this.currentProgressState) {
      this.textEl.innerHTML = message;
      return;
    }

    this.currentProgressState = progressState;

    await this.waitForClassTransition('changing');
    this.textEl.innerHTML = message;
    this.textEl.classList.remove('changing');

    await this.waitForClassTransition('changed');
    this.textEl.classList.remove('changed');
  }

  waitForClassTransition(className) {
    if (!this.textEl) return Promise.resolve();
    this.textEl.classList.add(className);

    return Promise.race([
      new Promise((resolve) => {
        const handleTransition = (event) => {
          if (event.target === event.currentTarget) {
            this.textEl.removeEventListener('transitionend', handleTransition);
            resolve();
          }
        };
        this.textEl.addEventListener('transitionend', handleTransition);
      }),
      new Promise((resolve) => setTimeout(resolve, 350)),
    ]);
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber();
  }
}

customElements.define('progress-bar', ProgressBar);

function updateCartSavings() {
  const saveEl = document.querySelector('.total__save');
  const saveValueEl = document.querySelector('.total__save-value');
  if (!saveEl || !saveValueEl) return;

  let totalSavings = 0;
  document.querySelectorAll('subscription-toggle').forEach((toggle) => {
    const input = toggle.querySelector('input[id^="subscription-"]');
    if (input && input.checked) {
      totalSavings += parseInt(toggle.dataset.saveAmount) || 0;
    }
  });

  if (totalSavings > 0) {
    saveValueEl.innerHTML = Shopify.formatMoney(totalSavings);
  } else {
    saveEl.style.display = 'none';
  }
}

const debouncedUpdateCartSavings = debounce(updateCartSavings, 50);

class SubscriptionToggle extends HTMLElement {
  constructor() {
    super();

    this.inputChangeHandler = this.inputChangeHandler.bind(this);
    this.onKeydownHandler = this.onKeydownHandler.bind(this);
    this.debouncedCartUpdate = debounce((event) => {
      this.updateCartQuantity(event);
    }, 50);
  }

  connectedCallback() {
    this.firstAvailableSellingPlanId = this.dataset.sellingPlanId;
    this.input = this.querySelector('input[id^="subscription-"]');
    this.isChecked = this.input?.checked ?? false;
    this.index = this.dataset.index;
    this.sellingPlanQty = this.dataset.sellingPlanQty;

    if (!this.input) return;
    this.input.addEventListener('change', this.inputChangeHandler);
    this.input.addEventListener('keydown', this.onKeydownHandler);
    this.toggleSubscriptionOptions(this.input.checked);
    debouncedUpdateCartSavings();
  }

  inputChangeHandler(event) {
    event.preventDefault();
    event.stopPropagation();

    this.debouncedCartUpdate(event);
  }

  onKeydownHandler(event) {
    event.stopPropagation();

    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.checked = !event.target.checked;
      event.target.dispatchEvent(new Event('change'));
    }
  }

  updateCartQuantity(event) {
    const newChecked = event.target.checked;
    if (this.isChecked == newChecked) return;

    this.toggleSubscriptionOptions(newChecked);

    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

    cartItems.updateQuantity(
      this.index,
      this.sellingPlanQty || 1,
      event,
      'subscription',
      ...(this.input.checked ? [this.firstAvailableSellingPlanId] : ['clear'])
    );
  }

  toggleSubscriptionOptions(isChecked) {
    const cartItem = this.closest('.cart-item') || this.closest('[id^="CartDrawer-Item-"]');
    if (!cartItem) return;

    const subscriptionOptionsWrapper = cartItem.querySelector('.cart-item__subscription-options-wrapper');
    if (subscriptionOptionsWrapper) {
      subscriptionOptionsWrapper.style.display = isChecked ? '' : 'none';
    }

    const priceWrapper = cartItem.querySelector('.cart-item__price-wrapper');
    if (priceWrapper) {
      const subscriptionPriceSale = priceWrapper.querySelector('.cart-item__price-subscription');
      const subscriptionPriceRegular = priceWrapper.querySelector('.cart-item__price-subscription-regular');
      const regularPrice = priceWrapper.querySelector('.cart-item__price-regular');

      if (isChecked) {
        if (subscriptionPriceSale) subscriptionPriceSale.style.display = '';
        if (subscriptionPriceRegular) subscriptionPriceRegular.style.display = '';
        if (regularPrice) regularPrice.style.display = 'none';
      } else {
        if (subscriptionPriceSale) subscriptionPriceSale.style.display = 'none';
        if (subscriptionPriceRegular) subscriptionPriceRegular.style.display = 'none';
        if (regularPrice) regularPrice.style.display = '';
      }
    }

    updateCartSavings();
  }
}

customElements.define('subscription-toggle', SubscriptionToggle);

class QuantityInputCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new Event('change', { bubbles: true });
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) {
      console.warn('[QuantityInput] Input not found');
      return;
    }

    this.input.addEventListener('change', this.onInputChange);

    const buttons = this.querySelectorAll('button');
    buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick);
    });

    this.validateQtyRules();
    // Only subscribe if pubsub is available
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.input) return;

    const previousValue = this.input.value;
    const button = event.currentTarget || event.target.closest('button') || event.target;

    if (button.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else if (button.name === "minus") {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && cartItems.updateQuantity) {
        const index = this.input.dataset.index;
        const variantId = this.input.dataset.quantityVariantId;
        cartItems.updateQuantity(index, this.input.value, event, this.input.name, variantId);
      } else {
        this.input.dispatchEvent(this.changeEvent);
      }
    }

    if (this.input.dataset.min === previousValue && button.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) {
        buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
      }
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.toggle('disabled', value >= max);
      }
    }
  }
}

customElements.define('quantity-input-cart-drawer', QuantityInputCartDrawer);